import moment from 'moment';

function isDateWithTime(date) {
  return date.includes('T');
}

export const computeIntervals = (includeDateIntervales, showTimeSelect) => {
  const hasIncludeIntervals = includeDateIntervales?.length > 0;
  if (hasIncludeIntervals) {
    const includeIntervals = includeDateIntervales
      .filter((range) => !!range.startDate && !!range.endDate)
      .map((range) => {
        if (!showTimeSelect && !range.startDate.includes('T')) {
          return {
            start: moment(range.startDate, 'YYYY-MM-DD').startOf('day').toDate(),
            end: moment(range.endDate, 'YYYY-MM-DD').endOf('day').toDate(),
          };
        }
        const { startDate, endDate } = range;
        if (showTimeSelect) {
          return {
            start: isDateWithTime(startDate) ? new Date(startDate) : moment(startDate, 'YYYY-MM-DD').startOf('day').toDate(),
            end: isDateWithTime(endDate) ? new Date(endDate) : moment(endDate, 'YYYY-MM-DD').endOf('day').toDate(),
          };
        }
        return { start: new Date(range.startDate), end: new Date(range.endDate) };
      });
    return { hasIncludeIntervals, includeIntervals };
  }

  return { hasIncludeIntervals };
};

const isInInterval = (date, { start, end }) => date >= start && date <= end;

/**
 * The days of the intervals, whole: react-datepicker checks a day at midnight, so an interval starting on
 * December 10th at 08:00 left the 10th out. The hours are filtered by getFilterTime.
 */
export const toWholeDays = (intervals) =>
  intervals?.map(({ start, end }) => ({ start: moment(start).startOf('day').toDate(), end: moment(end).endOf('day').toDate() }));

/**
 * Hours the picker lists for a date and time field restricted to intervals: react-datepicker's
 * includeDateIntervals only filters the days. Undefined when every hour is allowed.
 * Without a chosen day, the picker lists today's hours, but a click on one keeps the day it opens on (`openToDate`).
 */
export function getFilterTime({ showTimeSelect, showTimeSelectOnly, hasIncludeIntervals, includeIntervals, selected, openToDate }) {
  if (!showTimeSelect || showTimeSelectOnly || !hasIncludeIntervals) return undefined;
  return (time) => {
    const date = !selected && openToDate ? moment(openToDate).set({ hour: time.getHours(), minute: time.getMinutes() }).toDate() : time;
    return includeIntervals.some((interval) => isInInterval(date, interval));
  };
}

/**
 * Moves a date chosen out of the intervals' hours to the nearest bound of its day: choosing the first day
 * of an interval starting at 08:00 keeps 00:00 otherwise.
 */
export function clampToIntervals(date, includeIntervals) {
  if (!date || !includeIntervals?.length || includeIntervals.some((interval) => isInInterval(date, interval))) return date;

  const wholeDays = toWholeDays(includeIntervals);
  const dayInterval = includeIntervals.find((_interval, index) => isInInterval(date, wholeDays[index]));
  if (!dayInterval) return date;
  return date < dayInterval.start ? dayInterval.start : dayInterval.end;
}

// Day first in Europe; ISO in English, as before, and for any other language
const dateFormatsByLang = {
  fr: 'dd/MM/yyyy',
  es: 'dd/MM/yyyy',
  it: 'dd/MM/yyyy',
  pt: 'dd/MM/yyyy',
  de: 'dd.MM.yyyy',
  en: 'yyyy-MM-dd',
};

/** Format shown and typed in the field (the saved value stays ISO) */
export function getDefaultDateFormat(lang, showTimeSelect) {
  const dateFormat = dateFormatsByLang[lang] || dateFormatsByLang.en;
  return showTimeSelect ? `${dateFormat} HH:mm` : dateFormat;
}

// Other ways of typing a day-first date. ISO too: the field showed it before, and without a list of formats
// react-datepicker reads an unknown typing with `new Date()`, in UTC: a day early in the Americas
const dayFirstInputs = ['dd/MM/yyyy', 'dd/MM/yy', 'dd.MM.yyyy', 'dd.MM.yy', 'dd-MM-yyyy', 'yyyy-MM-dd'];

/**
 * Formats react-datepicker reads the typing with, the format shown first. English keeps its single ISO format.
 * react-datepicker keeps the last format that reads a typing: a typing reads with a single one of them.
 */
export function getDefaultInputFormats(lang, showTimeSelect) {
  const shown = getDefaultDateFormat(lang, showTimeSelect);
  if (!(lang in dateFormatsByLang) || lang === 'en') return shown;

  const others = dayFirstInputs.filter((format) => format !== dateFormatsByLang[lang]);
  const withHours = (formats) => (showTimeSelect ? formats.map((format) => `${format} HH:mm`) : formats);
  return [shown, ...withHours(others)];
}
